import type { LightingControl } from "../world-ui/lighting/control.ts";
import type { WeatherReport } from "../report.ts";

/**
 * What the scene reads of the weather view each frame.
 */
export interface WeatherReadings {
  readonly emitters: number;
  readonly budget: number;
  readonly dim: number;
  readonly windShown?: { readonly wisps: number; readonly dashes: number; readonly clouds: number };
  readonly strength?: number;
  readonly wet?: number;
  readonly fading?: boolean;
  readonly puddleWater?: number;
}

/**
 * Publishes the weather view's readings: to the page's report, and the dim to the light layer's control, which holds the darker
 * of the sky and the weather. Writes numbers into the objects given, so a frame allocates nothing.
 *
 * @param view The weather view.
 * @param report The page's weather report.
 * @param lighting The light layer's control.
 */
export function publishWeather(view: WeatherReadings, report: WeatherReport, lighting: Pick<LightingControl, "weatherDim">): void {
  report.emitters = view.emitters;
  report.budget = view.budget;
  report.dim = view.dim;
  report.wisps = view.windShown?.wisps ?? 0;
  report.dashes = view.windShown?.dashes ?? 0;
  report.clouds = view.windShown?.clouds ?? 0;
  report.strength = view.strength ?? 0;
  report.wet = view.wet ?? 0;
  report.fading = view.fading ?? false;
  report.water = view.puddleWater ?? 0;
  lighting.weatherDim = view.dim;
}

/**
 * Clears what a stage published, when the stage ends or a transfer begins, so the next stage does not start dimmed by weather
 * it has not drawn.
 *
 * @param report The page's weather report.
 * @param lighting The light layer's control.
 */
export function clearWeather(report: WeatherReport, lighting: Pick<LightingControl, "weatherDim" | "roofStamps">): void {
  report.emitters = 0;
  report.budget = 0;
  report.dim = 0;
  report.wisps = 0;
  report.dashes = 0;
  report.clouds = 0;
  report.strength = 0;
  report.wet = 0;
  report.fading = false;
  report.water = 0;
  lighting.weatherDim = 0;
  lighting.roofStamps = [];
}
